import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { ITextLocaleMap } from '../models/workflows.models';

/** Indica si hay texto útil (string legacy o algún locale en mapa). */
export function hasWorkflowLocaleText(value: string | ITextLocaleMap | null | undefined): boolean {
	if (value == null) {
		return false;
	}
	if (typeof value === 'string') {
		return value.trim().length > 0;
	}
	return Object.values(value).some((v) => String(v ?? '').trim().length > 0);
}

/** Todos los locales permitidos tienen texto no vacío (tras trim). */
export function isWorkflowLocaleMapComplete(map: ITextLocaleMap, allowedLocales: readonly string[]): boolean {
	return allowedLocales.every((locale) => String(map[locale] ?? '').trim().length > 0);
}

/** Normaliza respuesta API (string u objeto) a mapa con solo locales permitidos. */
export function normalizeWorkflowLocaleText(
	value: string | ITextLocaleMap | null | undefined,
	allowedLocales: readonly string[],
): ITextLocaleMap {
	const base = Object.fromEntries(allowedLocales.map((l) => [l, ''])) as ITextLocaleMap;
	if (value == null) {
		return base;
	}
	if (typeof value === 'string') {
		const out = { ...base };
		for (const l of allowedLocales) {
			out[l] = value;
		}
		return out;
	}
	if (typeof value === 'object') {
		const out = { ...base };
		for (const l of allowedLocales) {
			const v = (value as ITextLocaleMap)[l];
			if (v !== undefined && v !== null) {
				out[l] = String(v);
			}
		}
		return out;
	}
	return base;
}

/**
 * Completa solo locales vacíos (tras trim) con el string del mismo idioma en `defaults`.
 * No modifica textos ya informados en el mapa normalizado.
 */
export function mergeEmptyWorkflowLocalesFromDefaults(
	normalized: ITextLocaleMap,
	allowedLocales: readonly string[],
	defaults: Readonly<ITextLocaleMap>,
): ITextLocaleMap {
	const out = { ...normalized } as ITextLocaleMap;
	for (const l of allowedLocales) {
		if (String(out[l] ?? '').trim().length === 0) {
			out[l] = String(defaults[l] ?? '');
		}
	}
	return out;
}

/**
 * Defaults solo si el campo API no tiene ningún locale con texto.
 * Configuración parcial (p. ej. `es: null`, `pt: "..."`) conserva vacíos sin rellenar defaults.
 */
export function mergeWorkflowLocalesFromDefaultsWhenUnconfigured(
	value: string | ITextLocaleMap | null | undefined,
	allowedLocales: readonly string[],
	defaults: Readonly<ITextLocaleMap>,
): ITextLocaleMap {
	const normalized = normalizeWorkflowLocaleText(value, allowedLocales);
	if (!hasWorkflowLocaleText(value)) {
		return mergeEmptyWorkflowLocalesFromDefaults(normalized, allowedLocales, defaults);
	}
	return normalized;
}

/** Bloque pending FINISH sin configurar en API (title y description sin texto). */
export function isFinishPendingBlockUnconfigured(
	pendingTitle: string | ITextLocaleMap | null | undefined,
	pendingDescription: string | ITextLocaleMap | null | undefined,
): boolean {
	return !hasWorkflowLocaleText(pendingTitle) && !hasWorkflowLocaleText(pendingDescription);
}

/** Hidrata `pending_title`: defaults si bloque vacío o si title vacío con description informada. */
export function normalizeFinishPendingTitleFromDetail(
	pendingTitle: string | ITextLocaleMap | null | undefined,
	pendingDescription: string | ITextLocaleMap | null | undefined,
	allowedLocales: readonly string[],
	defaults: Readonly<ITextLocaleMap>,
): ITextLocaleMap {
	const normalized = normalizeWorkflowLocaleText(pendingTitle, allowedLocales);
	if (isFinishPendingBlockUnconfigured(pendingTitle, pendingDescription)) {
		return mergeEmptyWorkflowLocalesFromDefaults(normalized, allowedLocales, defaults);
	}
	if (!hasWorkflowLocaleText(pendingTitle)) {
		return mergeEmptyWorkflowLocalesFromDefaults(normalized, allowedLocales, defaults);
	}
	return normalized;
}

/** Hidrata `pending_description`: defaults solo si title y description vienen sin configurar. */
export function normalizeFinishPendingDescriptionFromDetail(
	pendingTitle: string | ITextLocaleMap | null | undefined,
	pendingDescription: string | ITextLocaleMap | null | undefined,
	allowedLocales: readonly string[],
	defaults: Readonly<ITextLocaleMap>,
): ITextLocaleMap {
	const normalized = normalizeWorkflowLocaleText(pendingDescription, allowedLocales);
	if (isFinishPendingBlockUnconfigured(pendingTitle, pendingDescription)) {
		return mergeEmptyWorkflowLocalesFromDefaults(normalized, allowedLocales, defaults);
	}
	return normalized;
}

/**
 * Título o descripción de documento GENÉRICO: aplica `defaults` por idioma y corrige respuestas legacy
 * (string único o el mismo texto en todos los locales) para que `en` use el valor en inglés de `defaults`.
 */
export function mergeGenericDocLocaleField(
	value: string | ITextLocaleMap | null | undefined,
	allowedLocales: readonly string[],
	defaults: Readonly<ITextLocaleMap>,
): ITextLocaleMap {
	const defaultsForAllowed = (): ITextLocaleMap => {
		const out = Object.fromEntries(allowedLocales.map((l) => [l, ''])) as ITextLocaleMap;
		for (const l of allowedLocales) {
			const v = defaults[l];
			if (v !== undefined) {
				out[l] = v;
			}
		}
		return out;
	};

	const n = normalizeWorkflowLocaleText(value, allowedLocales);
	if (!hasWorkflowLocaleText(n)) {
		return defaultsForAllowed();
	}

	const trimmed = allowedLocales.map((l) => String(n[l] ?? '').trim());
	const nonEmpty = trimmed.filter((t) => t.length > 0);
	const allSame =
		nonEmpty.length > 0 &&
		nonEmpty.every((t) => t === nonEmpty[0]) &&
		trimmed.every((t) => t.length === 0 || t === nonEmpty[0]);

	if (allSame && nonEmpty.length === allowedLocales.length) {
		const copy = nonEmpty[0];
		const out = { ...n };
		if (allowedLocales.includes('es')) {
			out['es'] = copy;
		}
		if (allowedLocales.includes('en')) {
			out['en'] = defaults['en'] ?? '';
		}
		return out;
	}

	if (allowedLocales.includes('en') && !String(n['en'] ?? '').trim() && String(n['es'] ?? '').trim()) {
		return { ...n, en: defaults['en'] ?? '' };
	}

	return n;
}

/** Texto a mostrar según idioma activo (detalle / listados). */
export function resolveWorkflowLocaleText(
	value: string | ITextLocaleMap | null | undefined,
	preferredLang: string,
	fallbackLang: string = 'es',
): string {
	if (value == null) {
		return '';
	}
	if (typeof value === 'string') {
		return value;
	}
	const map = value as ITextLocaleMap;
	const seen = new Set<string>();
	const order: string[] = [];
	for (const k of [preferredLang, fallbackLang, ...Object.keys(map)]) {
		if (k != null && !seen.has(k)) {
			seen.add(k);
			order.push(k);
		}
	}
	for (const k of order) {
		const t = map[k]?.trim();
		if (t) {
			return t;
		}
	}
	return '';
}

/** Compara dos mapas idioma→texto (tras trim) para los locales permitidos del workflow. */
export function workflowLocaleMapsEqual(
	leftMap: ITextLocaleMap,
	rightMap: ITextLocaleMap,
	allowedLocales: readonly string[],
): boolean {
	return allowedLocales.every(
		(locale) => String(leftMap[locale] ?? '').trim() === String(rightMap[locale] ?? '').trim(),
	);
}

/** True si el valor del locale (trim) coincide con el default de ese idioma. */
export function isWorkflowLocaleValueAtDefault(
	value: string | null | undefined,
	defaultValue: string | null | undefined,
): boolean {
	return String(value ?? '').trim() === String(defaultValue ?? '').trim();
}

/**
 * True si todos los locales están vacíos o todos coinciden con sus defaults (campo no personalizado).
 * Mezcla vacío + default cuenta como no-default (personalización a medias).
 */
export function isWorkflowLocaleMapAtDefaultsOrEmpty(
	map: ITextLocaleMap,
	defaults: Readonly<ITextLocaleMap>,
	allowedLocales: readonly string[],
): boolean {
	const allEmpty = allowedLocales.every((locale) => String(map[locale] ?? '').trim().length === 0);
	if (allEmpty) {
		return true;
	}
	return allowedLocales.every((locale) =>
		isWorkflowLocaleValueAtDefault(map[locale], defaults[locale]),
	);
}

/**
 * Campo personalizado: algún locale con texto distinto del default, o vacío mientras otros no lo están.
 * Usado para badges all-or-nothing tras prefill de defaults (NIF-762 / comentario 124138).
 */
export function isWorkflowLocaleMapCustomized(
	map: ITextLocaleMap,
	defaults: Readonly<ITextLocaleMap>,
	allowedLocales: readonly string[],
): boolean {
	return !isWorkflowLocaleMapAtDefaultsOrEmpty(map, defaults, allowedLocales);
}

/**
 * Si `changedLocale` dejó de ser el default, limpia locales hermanos que aún tienen su default.
 * No toca locales ya vacíos o con texto custom. Devuelve mapa nuevo (inmutable).
 */
export function clearSiblingLocalesStillAtDefault(
	map: ITextLocaleMap,
	changedLocale: string,
	defaults: Readonly<ITextLocaleMap>,
	allowedLocales: readonly string[],
): ITextLocaleMap {
	const changedValue = String(map[changedLocale] ?? '').trim();
	const changedDefault = String(defaults[changedLocale] ?? '').trim();
	if (changedValue === changedDefault) {
		return { ...map };
	}
	const out = { ...map } as ITextLocaleMap;
	for (const locale of allowedLocales) {
		if (locale === changedLocale) {
			continue;
		}
		if (isWorkflowLocaleValueAtDefault(out[locale], defaults[locale])) {
			out[locale] = '';
		}
	}
	return out;
}

/**
 * Si el mapa sigue en defaults (o vacío), devuelve mapa vacío; si no, el mapa tal cual.
 * Evita persistir copies Tecalis en API al guardar sin cambios (NIF-762).
 */
export function stripWorkflowLocaleMapIfStillDefaults(
	map: ITextLocaleMap,
	defaults: Readonly<ITextLocaleMap>,
	allowedLocales: readonly string[],
): ITextLocaleMap {
	if (isWorkflowLocaleMapAtDefaultsOrEmpty(map, defaults, allowedLocales)) {
		return emptyWorkflowLocaleMap(allowedLocales);
	}
	return { ...map };
}

/** Devuelve un mapa con cadena vacía en cada idioma permitido. */
export function emptyWorkflowLocaleMap(allowedLocales: readonly string[]): ITextLocaleMap {
	return Object.fromEntries(allowedLocales.map((locale) => [locale, ''])) as ITextLocaleMap;
}

/** Serializa valor de formulario (objeto plano) al payload. */
export function serializeWorkflowLocaleFormValue(value: unknown, allowedLocales: readonly string[]): ITextLocaleMap {
	const out = Object.fromEntries(allowedLocales.map((l) => [l, ''])) as ITextLocaleMap;
	if (value && typeof value === 'object' && !Array.isArray(value)) {
		for (const l of allowedLocales) {
			const v = (value as ITextLocaleMap)[l];
			out[l] = v != null ? String(v) : '';
		}
	}
	return out;
}

/**
 * Campo i18n opcional para API: `undefined` si todo vacío; mapa solo si todos los locales tienen texto.
 * Evita error 1010 del back cuando hay texto parcial en un mapa multi-idioma.
 */
export function serializeOptionalWorkflowLocaleForApi(
	value: unknown,
	allowedLocales: readonly string[],
): ITextLocaleMap | undefined {
	const map = serializeWorkflowLocaleFormValue(value, allowedLocales);
	if (!hasWorkflowLocaleText(map)) {
		return undefined;
	}
	if (!isWorkflowLocaleMapComplete(map, allowedLocales)) {
		return undefined;
	}
	return map;
}

/** Al menos un locale con texto no vacío (tras trim). */
export function atLeastOneLocaleTextValidator(): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		const g = control as FormGroup;
		if (!g?.controls) {
			return null;
		}
		const has = Object.values(g.controls).some((c) => (c.value ?? '').toString().trim().length > 0);
		return has ? null : { required: true };
	};
}

/** Cada locale debe tener texto no vacío (tras trim); para FormControl hijo de un FormGroup por idioma. */
export function trimmedLocaleTextRequiredValidator(): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		const v = (control.value ?? '').toString().trim();
		return v.length > 0 ? null : { required: true };
	};
}

/** Valor canónico del `tec-text-editor` para FormControl (solo `{ html }`, objeto nuevo). */
export function normalizeRichTextEditorFormValue(value: unknown): { html: string } {
	return { html: extractWorkflowRichTextHtml(value) };
}

/** Indica si el control tiene forma legacy (string, `{ html, attachments }`, etc.) y debe normalizarse al cargar. */
export function needsRichTextEditorFormValuePatch(value: unknown): boolean {
	if (value == null) {
		return true;
	}
	if (typeof value === 'string') {
		return true;
	}
	if (typeof value === 'object') {
		if (!('html' in value)) {
			return true;
		}
		if ('attachments' in value) {
			return true;
		}
		return Object.keys(value as object).some((key) => key !== 'html');
	}
	return true;
}

/** Extrae HTML útil de un valor rich-text (`{ html }`, string legacy o vacío). */
export function extractWorkflowRichTextHtml(label: unknown): string {
	if (typeof label === 'string') return label.trim();

	if (label && typeof label === 'object' && 'html' in label) {
		return String((label as { html?: string }).html ?? '').trim();
	}

	return '';
}

/** Obligatorio para controles rich-text por idioma (label `{ html }` o string). */
export function trimmedRichTextLocaleRequiredValidator(): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		return extractWorkflowRichTextHtml(control.value).length > 0 ? null : { required: true };
	};
}

/**
 * Primer idioma (orden de `allowedLocales`) sin texto útil o con control inválido.
 * Cubre grupos con validación solo a nivel padre (p. ej. todos los locales obligatorios vía FormGroup).
 */
export function firstMissingLocaleTextId(
	group: FormGroup | null | undefined,
	allowedLocales: readonly string[],
): string | null {
	if (!group) {
		return null;
	}
	for (const l of allowedLocales) {
		const c = group.get(l);
		if (!c) {
			continue;
		}
		if (c.invalid) {
			return l;
		}
		const v = (c.value ?? '').toString().trim();
		if (v.length === 0) {
			return l;
		}
	}
	return null;
}

/** Todos los controles hijo (por locale) deben tener texto no vacío (tras trim). */
export function allLocalesTextFilledValidator(): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		const g = control as FormGroup;
		if (!g?.controls) {
			return null;
		}
		const allFilled = Object.values(g.controls).every((c) => (c.value ?? '').toString().trim().length > 0);
		return allFilled ? null : { required: true };
	};
}

/**
 * Para pautas/instrucciones opcionales: si ningún locale tiene texto, el grupo es válido.
 * Si hay texto en algún idioma, todos los locales deben tener texto (coherente con obligatoriedad multi-idioma).
 */
export function localeTextRequireAllLocalesWhenAnyFilledValidator(): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		const g = control as FormGroup;
		if (!g?.controls) {
			return null;
		}
		const values = Object.values(g.controls).map((c) => (c.value ?? '').toString().trim());
		const anyFilled = values.some((v) => v.length > 0);
		if (!anyFilled) {
			return null;
		}
		const allFilled = values.every((v) => v.length > 0);
		return allFilled ? null : { required: true };
	};
}
